// @ts-expect-error Activity is new in React 19.2
import React, { useRef, Activity, startTransition } from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useTheme } from '~common/theme/theme-provider'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { SimpleTabsLogic } from './logic'
import type { SimpleTabsLogicDeps, Tab, TabInstance } from './types'
import ExportButton from '~/features/ui-components/export-button'
import { AutoScanButton } from '~/features/ui-components/auto-scan-button'
import { SettingsButton } from '~/features/ui-components/settings-button'
import { LoadingIndicator } from '~common/components/loading'
import { Button } from '~common/components/button'
import {
    SearchComponent,
    type SearchComponentRef,
} from '~/features/ui-components/search'
import {
    DatePicker,
    type DatePickerRef,
} from '~/features/ui-components/date-picker'
import { useCacheValue } from '~/cache-context'
import { ManageBankAccounts } from '~/features/ui-components/manage-bank-accounts'
import { ImportProgressBar } from '../import-progress-bar'
import { Icon } from '~common/components/icon'
import { TooltipComponent } from '../../../../firebase-functions/src/common/components/tooltip-component'
import type { BankTransaction } from '~/storage/types'
import type { DocumentTransactionAssociation } from '~/storage/types'
import { formatCurrencyForDisplay } from '../../../../firebase-functions/src/common/utils/currency-normalization/currency-normalization'
import { ActiveTimeDisplay } from './active-time-display'
import { DateRange } from '~/features/email-importer/services/utils'
import { SIDEBAR_WIDTH, SMALL_SIDEBAR_WIDTH } from './constants'
import { ThemeToggle } from '../theme-toggle'
import { GmailAccountSelector } from '~/features/ui-components/gmail-account-selector'

const TAB_BAR_HEIGHT = 60

export interface SimpleTabsLayoutProps extends SimpleTabsLogicDeps {}

export const SimpleTabsLayout: React.FC<SimpleTabsLayoutProps> = (props) => {
    const { t } = useTranslation('split-tabs')
    const { logic, state } = useLogic(SimpleTabsLogic, props)
    const { isDark, toggleTheme, theme } = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const mainContentRef = useRef<HTMLDivElement>(null)
    const searchComponentRef = useRef<SearchComponentRef>(null)
    const datePickerRef = useRef<DatePickerRef>(null)
    const stats = useCacheValue('stats')
    const invoicesCounter = stats?.invoicesCount ?? 0
    const entitlement = useCacheValue('entitlement')
    const remainingInvoices = entitlement
        ? (entitlement.invoicesRemainingSubscription ?? 0) +
          (entitlement.invoicesRemainingConsumable ?? 0)
        : 0

    const transactions = useCacheValue('transactions') ?? {}
    const matchesByTransaction = useCacheValue('matchesByTransaction') ?? {}
    const documentTransactionAssociations =
        useCacheValue('documentTransactionAssociations') ?? {}

    // Calculate unmatched transactions stats
    const allTransactions = Object.values(transactions) as BankTransaction[]
    const totalTransactions = allTransactions.length

    let unmatchedCountIncludingSnoozed = 0
    let unmatchedCountExcludingSnoozed = 0
    let volumeExcludingSnoozed = 0
    let volumeIncludingSnoozed = 0
    let totalTransactionsExcludingSnoozed = 0
    const currencyCounts: Record<string, number> = {}

    for (const transaction of allTransactions) {
        // Track currency usage
        const currency = transaction.currency || 'USD'
        currencyCounts[currency] = (currencyCounts[currency] || 0) + 1

        // Check if transaction is matched (has user_confirmed association)
        const assocIds = matchesByTransaction[transaction.id] || []
        const associations = assocIds
            .map((id) => documentTransactionAssociations[id])
            .filter((assoc): assoc is DocumentTransactionAssociation =>
                Boolean(assoc),
            )
        const isMatched = associations.some(
            (assoc) => assoc.method === 'user_confirmed',
        )

        const isSnoozed = transaction.status === 'snoozed'
        // Calculate volume (use absolute value)
        const amount = Math.abs(transaction.amount)

        if (!isMatched) {
            unmatchedCountIncludingSnoozed++
            volumeIncludingSnoozed += amount
            if (!isSnoozed) {
                unmatchedCountExcludingSnoozed++
                volumeExcludingSnoozed += amount
            }
            totalTransactionsExcludingSnoozed++
        }
    }

    // Determine primary currency (most common)
    const primaryCurrency =
        Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        'USD'

    const unmatchedPercentageExcludingSnoozed =
        totalTransactions > 0
            ? Math.round(
                  (unmatchedCountExcludingSnoozed / totalTransactions) * 100,
              )
            : 0

    const unmatchedPercentageIncludingSnoozed =
        totalTransactions > 0
            ? Math.round(
                  (unmatchedCountIncludingSnoozed / totalTransactions) * 100,
              )
            : 0

    const transactionStats = {
        unmatchedCountExcludingSnoozed,
        unmatchedCountIncludingSnoozed,
        unmatchedPercentageExcludingSnoozed,
        unmatchedPercentageIncludingSnoozed,
        volumeExcludingSnoozed,
        volumeIncludingSnoozed,
        primaryCurrency,
    }

    // Keep track of all rendered tab components to preserve their state
    const [renderedComponents, setRenderedComponents] = React.useState<
        Map<string, React.ReactNode>
    >(new Map())

    // Track which components have been activated (lazy initialization)
    const [activatedComponents, setActivatedComponents] = React.useState<
        Set<string>
    >(new Set())

    // Track the last focused menu item (regular tab) for breadcrumb display
    const [lastFocusedMenuId, setLastFocusedMenuId] = React.useState<
        string | null
    >(null)
    const [isSidebarHovered, setIsSidebarHovered] = React.useState(false)

    // Get all available panes (tabs + spawned panes)
    const allPanes = props.tabs.map((tab) => ({
        id: tab.id,
        title: tab.title,
        isSpawned: false,
    }))

    // Lazy initialization: create components only when they become active
    React.useEffect(() => {
        const newActivatedComponents = new Set(activatedComponents)
        let hasNewActivations = false

        // Check for newly activated panes
        for (const paneId of state.activePaneIds) {
            if (!activatedComponents.has(paneId)) {
                newActivatedComponents.add(paneId)
                hasNewActivations = true
            }
        }

        if (hasNewActivations) {
            setActivatedComponents(newActivatedComponents)
        }
    }, [state.activePaneIds, activatedComponents])

    // Create components for activated panes
    React.useEffect(() => {
        setRenderedComponents((prevComponents) => {
            const newComponents = new Map(prevComponents)
            let hasChanges = false

            // Create or update components for activated panes
            for (const paneId of activatedComponents) {
                // Check if it's a regular tab
                const tab = props.tabs.find((t) => t.id === paneId)
                if (tab) {
                    if (!prevComponents.has(paneId)) {
                        const renderedComponent = tab.render()
                        newComponents.set(paneId, renderedComponent)
                        hasChanges = true
                    }
                } else {
                    // Check if it's a spawned pane
                    const spawnedPane = state.spawnedPanes.find(
                        (p) => p.id === paneId,
                    )
                    if (spawnedPane) {
                        // Always update spawned panes to reflect render function changes
                        newComponents.set(paneId, spawnedPane.render())
                        hasChanges = true
                    }
                }
            }

            // Remove components for tabs/panes that no longer exist
            const currentIds = new Set([
                ...props.tabs.map((tab) => tab.id),
                ...state.spawnedPanes.map((pane) => pane.id),
            ])

            for (const [id] of newComponents) {
                if (!currentIds.has(id)) {
                    newComponents.delete(id)
                    hasChanges = true
                }
            }

            return hasChanges ? newComponents : prevComponents
        })
    }, [props.tabs, state.spawnedPanes, activatedComponents])

    // Update last focused menu item only when a regular tab is focused
    React.useEffect(() => {
        if (state.focusedPaneId) {
            const isMenuTab = props.tabs.some(
                (t) => t.id === state.focusedPaneId,
            )
            if (isMenuTab) {
                setLastFocusedMenuId(state.focusedPaneId)
            }
        }
    }, [state.focusedPaneId, props.tabs])

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                const currentState = logic.getState()
                if (currentState.activePaneIds.length > 1) {
                    const topRightPaneId =
                        currentState.activePaneIds[
                            currentState.activePaneIds.length - 1
                        ]
                    // Only close if it's on the right side (index > 0)
                    if (
                        currentState.activePaneIds.indexOf(topRightPaneId) > 0
                    ) {
                        logic.closePane(topRightPaneId)
                        event.preventDefault()
                    }
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [logic])

    const handleTabClick = (tabId: string, event?: React.MouseEvent) => {
        startTransition(() => {
            logic.handleTabClick(tabId, event?.shiftKey ?? false)
        })
    }

    const handleCloseSpawnedPane = (paneId: string) => {
        logic.closePane(paneId)
    }

    const handlePaneClick = (paneId: string) => {
        logic.focusPane(paneId)
    }

    const handleSeparatorMouseDown = (
        event: React.MouseEvent<HTMLDivElement>,
    ) => {
        event.preventDefault()
        const startX = event.clientX
        const containerWidth = mainContentRef.current?.clientWidth ?? 0
        const startPosition = state.paneSplitPosition

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - startX
            const deltaPercent = (deltaX / containerWidth) * 100
            const newPosition = startPosition + deltaPercent
            logic.setPaneSplitPosition(newPosition)
        }

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            // Ensure final position is saved to localStorage on drop
            const finalPosition = logic.getState().paneSplitPosition
            localStorage.setItem(
                'split-tabs-pane-split-position',
                finalPosition.toString(),
            )
        }

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
    }

    const BreadCrumbComponent = () => {
        return (
            <BreadCrumb>
                {lastFocusedMenuId &&
                    props.tabs.find((t) => t.id === lastFocusedMenuId)?.title}
            </BreadCrumb>
        )
    }

    return (
        <Root ref={containerRef}>
            {/* Sidebar */}
            <Sidebar
                onMouseEnter={() => setIsSidebarHovered(true)}
                onMouseLeave={() => setIsSidebarHovered(false)}
                isOpen={state.isSidebarOpen || isSidebarHovered}
            >
                <SidbarTopArea>
                    <SidebarToggleBox
                        isOpen={state.isSidebarOpen || isSidebarHovered}
                    >
                        <Button
                            onClick={() => logic.toggleSidebar()}
                            onMouseEnter={() => setIsSidebarHovered(true)}
                            icon={
                                !state.isSidebarOpen
                                    ? 'caret-double-right'
                                    : 'caret-double-left'
                            }
                            variant="ghost"
                            size="medium"
                        />
                        {(state.isSidebarOpen || isSidebarHovered) && (
                            <BreadCrumbComponent />
                        )}
                    </SidebarToggleBox>
                    <SidebarList role="tablist" aria-label="Main navigation">
                        {props.tabs.map((pane) => {
                            let counter: number | undefined = undefined
                            if (pane.id === 'invoices') {
                                counter = invoicesCounter ?? 0
                            } else if (pane.id === 'transactions') {
                                counter = totalTransactions ?? 0
                            }
                            const isActive = state.activePaneIds.includes(
                                pane.id,
                            )
                            const isExpanded =
                                state.isSidebarOpen || isSidebarHovered
                            return (
                                <SidebarItem
                                    key={pane.id}
                                    isActive={isActive}
                                    isExpanded={isExpanded}
                                    aria-selected={isActive}
                                    onClick={(
                                        event: React.MouseEvent<HTMLButtonElement>,
                                    ) => handleTabClick(pane.id, event)}
                                >
                                    <SidebarItemContent>
                                        {pane.icon && (
                                            <Icon
                                                name={pane.icon}
                                                size="medium"
                                                iconColor={
                                                    isActive
                                                        ? 'info'
                                                        : 'secondary'
                                                }
                                            />
                                        )}
                                        {isExpanded && (
                                            <span>{pane.title}</span>
                                        )}
                                    </SidebarItemContent>
                                    {isExpanded &&
                                        counter != null &&
                                        counter > 0 &&
                                        (counter === -1 ? (
                                            <LoadingIndicator
                                                size="small"
                                                variant="neutral"
                                            />
                                        ) : (
                                            <Counter>{counter}</Counter>
                                        ))}
                                </SidebarItem>
                            )
                        })}
                    </SidebarList>

                    {/* Stats Section */}
                    {(state.isSidebarOpen || isSidebarHovered) && (
                        <StatsSection>
                            <StatsTitle>{t('stats.title')}</StatsTitle>
                            <StatItem>
                                <StatLabel>{t('stats.unmatched')}</StatLabel>
                                <TooltipComponent
                                    content={
                                        <div>
                                            <div>
                                                {t('stats.excludingSnoozed')}{' '}
                                                {
                                                    transactionStats.unmatchedCountExcludingSnoozed
                                                }{' '}
                                                (
                                                {
                                                    transactionStats.unmatchedPercentageExcludingSnoozed
                                                }
                                                %)
                                            </div>
                                            <div>
                                                {t('stats.includingSnoozed')}{' '}
                                                {
                                                    transactionStats.unmatchedCountIncludingSnoozed
                                                }{' '}
                                                (
                                                {
                                                    transactionStats.unmatchedPercentageIncludingSnoozed
                                                }
                                                %)
                                            </div>
                                        </div>
                                    }
                                    position="right"
                                    size="small"
                                >
                                    <StatValue $cursor="help">
                                        {
                                            transactionStats.unmatchedCountExcludingSnoozed
                                        }{' '}
                                        (
                                        {
                                            transactionStats.unmatchedPercentageExcludingSnoozed
                                        }
                                        %)
                                    </StatValue>
                                </TooltipComponent>
                            </StatItem>
                            <StatItem>
                                <StatLabel>
                                    {t('stats.remainingVolume')}
                                </StatLabel>
                                <TooltipComponent
                                    content={
                                        <div>
                                            <div>
                                                {t('stats.excludingSnoozed')}{' '}
                                                {formatCurrencyForDisplay(
                                                    transactionStats.volumeExcludingSnoozed,
                                                    transactionStats.primaryCurrency,
                                                )}
                                            </div>
                                            <div>
                                                {t('stats.includingSnoozed')}{' '}
                                                {formatCurrencyForDisplay(
                                                    transactionStats.volumeIncludingSnoozed,
                                                    transactionStats.primaryCurrency,
                                                )}
                                            </div>
                                        </div>
                                    }
                                    position="right"
                                    size="small"
                                >
                                    <StatValue $cursor="help">
                                        {formatCurrencyForDisplay(
                                            transactionStats.volumeExcludingSnoozed,
                                            transactionStats.primaryCurrency,
                                        )}
                                    </StatValue>
                                </TooltipComponent>
                            </StatItem>
                            <StatItem>
                                <StatLabel>{t('stats.activeTime')}</StatLabel>
                                <ActiveTimeDisplay
                                    initialSeconds={state.activeTimeSeconds}
                                />
                            </StatItem>
                        </StatsSection>
                    )}
                </SidbarTopArea>
                <SidebarBottomArea
                    isOpen={state.isSidebarOpen || isSidebarHovered}
                >
                    <ThemeToggle
                        showLabel={isSidebarHovered || state.isSidebarOpen}
                    />
                    <SettingsButton
                        showLabel={isSidebarHovered || state.isSidebarOpen}
                    />
                </SidebarBottomArea>
            </Sidebar>
            <ContentArea>
                <TabBarContainer isOpen={state.isSidebarOpen}>
                    <LeftSide>
                        {!state.isSidebarOpen && (
                            <MenuSection>
                                <BreadCrumbComponent />
                            </MenuSection>
                        )}
                        {/* Search Section - only show when there are active panes */}
                        {state.activePaneIds.length > 0 && (
                            <SearchSection>
                                <SearchComponent
                                    ref={searchComponentRef}
                                    placeholder={state.searchPlaceholder}
                                    onUpdate={(value) => {
                                        logic.performSearch(
                                            value,
                                            datePickerRef.current?.getValue() as DateRange,
                                        )
                                    }}
                                    onConfirm={(searchTerm: string) => {
                                        const dateRange =
                                            datePickerRef.current?.getValue()
                                        logic.performSearch(
                                            searchTerm,
                                            dateRange as DateRange,
                                            false,
                                            true,
                                        )
                                    }}
                                    initValue={state.searchTerms}
                                    width="230px"
                                    size="medium"
                                    icon={logic.getSearchIcon()}
                                    disableOnBlur={true}
                                    autoFocus={true}
                                />
                                <DatePicker
                                    ref={datePickerRef}
                                    size="medium"
                                    placeholder={t('placeholders.dateRange')}
                                    onChange={(dateRange: DateRange | Date) => {
                                        logic.performSearch(
                                            searchComponentRef.current?.getValue() ??
                                                '',
                                            dateRange as DateRange,
                                        )
                                    }}
                                    initValue={state.searchDateRange}
                                    icon="calendar"
                                    onConfirm={(
                                        dateRange: DateRange | Date | undefined,
                                    ) => {
                                        const searchTerm =
                                            searchComponentRef.current?.getValue() ??
                                            ''
                                        logic.performSearch(
                                            searchTerm,
                                            dateRange as DateRange,
                                            false,
                                            true,
                                        )
                                    }}
                                    clearOnClick={() =>
                                        logic.performSearch(
                                            undefined,
                                            undefined,
                                            true,
                                        )
                                    }
                                    width="180px"
                                    disableOnBlur={true}
                                    resizable="horizontal"
                                />
                                {state.focusedPaneId === 'gmail-search' && (
                                    <GmailAccountSelector />
                                )}
                            </SearchSection>
                        )}
                    </LeftSide>

                    <RightSide>
                        <Button
                            label={remainingInvoices.toString()}
                            variant="secondary"
                            size="medium"
                            onClick={() =>
                                props.services.globalEvents.emit({
                                    type: 'openModal',
                                    modalType: 'pricing',
                                    metadata: {
                                        offerId: 'upgrade',
                                    },
                                })
                            }
                        />
                        {!state.activePaneIds.includes('transactions') && (
                            <AutoScanButton />
                        )}
                        {state.activePaneIds.includes('transactions') && (
                            <ManageBankAccounts />
                        )}
                        <ExportButton
                            label={t('buttons.export')}
                            variant="primary"
                        />
                    </RightSide>
                </TabBarContainer>
                <MainContentArea
                    ref={mainContentRef}
                    isOpen={state.isSidebarOpen}
                >
                    {/* Render all components to preserve their state */}
                    {Array.from(renderedComponents.entries()).map(
                        ([tabId, component], index) => {
                            const isActive = state.activePaneIds.includes(tabId)
                            const activeIndex =
                                state.activePaneIds.indexOf(tabId)

                            // Get version from spawned pane if it exists
                            const spawnedPane = state.spawnedPanes.find(
                                (p) => p.id === tabId,
                            )
                            const version = spawnedPane?.version ?? 0
                            const componentKey = `${tabId}-${version}`

                            // Layout:
                            // - First pane (index 0): uses paneSplitPosition% when multiple panes, 100% when single pane
                            // - Right-side stack (index > 0): all share the remaining (100 - paneSplitPosition)%, only top-most visible
                            const activeCount = state.activePaneIds.length
                            const isSinglePane = activeCount === 1
                            const isLeftPane = isActive && activeIndex === 0
                            const isRightPane = isActive && activeIndex > 0
                            const isTopOfStack =
                                isRightPane &&
                                activeIndex === state.activePaneIds.length - 1

                            const splitPosition = state.paneSplitPosition
                            const leftPosition = isLeftPane
                                ? '0%'
                                : isRightPane
                                  ? `${splitPosition}%`
                                  : '-9999px'
                            const width = isLeftPane
                                ? isSinglePane
                                    ? '100%'
                                    : `${splitPosition}%`
                                : isRightPane
                                  ? `${100 - splitPosition}%`
                                  : '100%'

                            return (
                                <Activity mode={'visible'} key={componentKey}>
                                    <ContentContainer
                                        key={componentKey}
                                        data-tab-id={tabId}
                                        onClick={() => handlePaneClick(tabId)}
                                        $left={leftPosition}
                                        $width={width}
                                        $zIndex={
                                            isLeftPane
                                                ? 1
                                                : isTopOfStack
                                                  ? 2
                                                  : 0
                                        }
                                        $opacity={
                                            isLeftPane
                                                ? 1
                                                : isTopOfStack
                                                  ? 1
                                                  : 0
                                        }
                                        $pointerEvents={
                                            isLeftPane || isTopOfStack
                                                ? 'auto'
                                                : 'none'
                                        }
                                    >
                                        <ImportProgressBar />
                                        {component}
                                        {(isTopOfStack || isLeftPane) &&
                                            state.activePaneIds.length > 1 &&
                                            (isTopOfStack || false) && (
                                                <PaneControls>
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            const currentState =
                                                                logic.getState()
                                                            const topRightPaneId =
                                                                currentState
                                                                    .activePaneIds[
                                                                    currentState
                                                                        .activePaneIds
                                                                        .length -
                                                                        1
                                                                ]
                                                            if (
                                                                currentState.activePaneIds.indexOf(
                                                                    topRightPaneId,
                                                                ) > 0
                                                            ) {
                                                                handleCloseSpawnedPane(
                                                                    topRightPaneId,
                                                                )
                                                            }
                                                        }}
                                                        icon="close"
                                                        tooltip={t(
                                                            'tooltips.closePane',
                                                        )}
                                                        size="small"
                                                        variant="glass"
                                                    />
                                                </PaneControls>
                                            )}
                                    </ContentContainer>
                                </Activity>
                            )
                        },
                    )}

                    {/* Draggable separator between panes */}
                    {state.activePaneIds.length > 1 && (
                        <PaneSeparator
                            $left={`${state.paneSplitPosition}%`}
                            onMouseDown={handleSeparatorMouseDown}
                        />
                    )}

                    {/* Show empty state when no tab is active */}
                    {state.activePaneIds.length === 0 &&
                        renderedComponents.size > 0 && (
                            <EmptyState>{t('emptyState')}</EmptyState>
                        )}
                </MainContentArea>
            </ContentArea>
        </Root>
    )
}

const Root = styled.div`
    display: flex;
    flex-direction: row;
    width: 100vw;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow: hidden;
`

const ContentArea = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
`

const TabBarContainer = styled.div<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[3]}`};
    height: ${TAB_BAR_HEIGHT}px;
    background-color: ${({ theme }) => theme.colors.background.bg2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    flex-shrink: 0;
    box-sizing: border-box;
    margin-left: ${({ isOpen }) =>
        isOpen ? SIDEBAR_WIDTH : SMALL_SIDEBAR_WIDTH}px;
`

const LeftSide = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[6]};
    flex-shrink: 0;
`

const SearchSection = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    justify-content: center;
`
const MainContentArea = styled.div<{ isOpen: boolean }>`
    flex: 1;
    min-height: 0;
    position: relative;
    overflow: hidden;
    transition: margin-left 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    margin-left: ${({ isOpen }) =>
        isOpen ? SIDEBAR_WIDTH : SMALL_SIDEBAR_WIDTH}px;
`

const ContentContainer = styled.div<{
    $left: string
    $width: string
    $zIndex: number
    $opacity: number
    $pointerEvents: string
}>`
    width: ${({ $width }) => $width};
    height: 100%;
    overflow: hidden;
    position: absolute;
    top: 0;
    left: ${({ $left }) => $left};
    z-index: ${({ $zIndex }) => $zIndex};
    opacity: ${({ $opacity }) => $opacity};
    pointer-events: ${({ $pointerEvents }) => $pointerEvents};
    border-right: none;
    transition: opacity 0.2s ease-in-out;
`

const PaneContainer = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &:not(:last-child) {
        border-right: 1px solid ${({ theme }) => theme.colors.border.primary};
    }
`

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    position: relative;
    z-index: 2;
`
const RightSide = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    flex: 1;
    justify-content: flex-end;
`

const PaneControls = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: ${({ theme }) => theme.spacing[1]};
    z-index: 3000;
`
const Counter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    color: ${({ theme }) => theme.colors.info};
    margin-left: ${({ theme }) => theme.spacing[1]};
`

const Sidebar = styled.aside<{ isOpen: boolean }>`
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.background.bg1};
    border-right: 1px solid ${({ theme }) => theme.colors.border.primary};
    z-index: 10;
    display: flex;
    flex-direction: column;
    transition: width 0.2s cubic-bezier(0.68, -0.1, 0.565, 1.15);
    width: ${({ isOpen }) => (isOpen ? SIDEBAR_WIDTH : SMALL_SIDEBAR_WIDTH)}px;
    z-index: 1000;
    align-items: stretch;
    height: 100%;
`

const SidebarList = styled.nav`
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing[2]};
    gap: ${({ theme }) => theme.spacing[4]};
    padding-top: ${({ theme }) => theme.spacing[4]};
`

const SidebarItem = styled.button<{ isActive?: boolean; isExpanded?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${({ isExpanded }) =>
        isExpanded ? 'space-between' : 'flex-start'};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    background: ${({ isActive, theme }) =>
        isActive ? theme.colors.background.bg2 : 'transparent'};
    color: ${({ theme }) => theme.colors.text.primary};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    cursor: pointer;
    height: 40px;
    text-align: left;
    font-weight: ${({ isActive, theme }) =>
        isActive ? theme.typography.fontWeight.semibold : 'normal'};
    &:hover {
        background-color: ${({ theme }) => theme.colors.background.bg2};
    }
`

const SidebarItemContent = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const StatsSection = styled.div`
    //prettier-ignore
    padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) =>
        theme.spacing[4]};
    border-top: 1px solid ${({ theme }) => theme.colors.greyScale[400]};
    margin-top: ${({ theme }) => theme.spacing[2]};
    width: ${SIDEBAR_WIDTH}px;
    box-sizing: border-box;
    animation: ${slideIn} 0.2s ease-in-out forwards;
`

const StatsTitle = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const StatItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
    margin-bottom: ${({ theme }) => theme.spacing[3]};

    &:last-child {
        margin-bottom: 0;
    }
`

const StatLabel = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
`

const StatValue = styled.div<{ $cursor?: string }>`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    cursor: ${({ $cursor }) => $cursor || 'inherit'};
`

const PaneSeparator = styled.div<{ $left: string }>`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 4px;
    margin-left: -2px;
    left: ${({ $left }) => $left};
    background-color: ${({ theme }) => theme.colors.border.primary};
    cursor: col-resize;
    z-index: 1000;
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: ${({ theme }) => theme.colors.border.focus};
    }

    &::before {
        content: '';
        position: absolute;
        left: -4px;
        right: -4px;
        top: 0;
        bottom: 0;
    }
`

const BreadCrumb = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    width: 100px;
    padding-left: ${({ theme }) => theme.spacing[3]};
`

const MenuSection = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const SidebarToggleBox = styled.div<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-left: ${({ theme }) => theme.spacing[3]};
    width: 100%;
    height: ${TAB_BAR_HEIGHT}px;
    gap: ${({ theme }) => theme.spacing[2]};
    width: ${({ isOpen }) => (isOpen ? SIDEBAR_WIDTH : SMALL_SIDEBAR_WIDTH)}px;
`

const SidbarTopArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
`

const SidebarBottomArea = styled.div<{ isOpen: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    justify-content: flex-end;
    padding: ${({ theme }) => theme.spacing[2]};
    box-sizing: border-box;
    height: 100%;
    align-items: flex-start;

    ${({ isOpen }) => css`
        width: ${isOpen ? SIDEBAR_WIDTH : SMALL_SIDEBAR_WIDTH}px;
    `}
`
